import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProgrammerContext } from '@/hid/ProgrammerContext';
import type { FlashProgress8051 } from '@/types/hid8051';

const ReadbackPanel8051: React.FC = () => {
  const { t } = useTranslation('webflash');
  const { programmer, selectedChip, status } = useProgrammerContext();
  const [address, setAddress] = useState('0000');
  const [length, setLength] = useState('');
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState<FlashProgress8051 | null>(null);
  const [readData, setReadData] = useState<Uint8Array | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isReady = status === 'connected' && programmer && selectedChip;
  const effectiveLength = length || (selectedChip ? selectedChip.flashSize.toString() : '0');

  const handleRead = useCallback(async (startAddr: number, readLen: number) => {
    if (!programmer) return;
    setReading(true);
    setError(null);
    setReadData(null);
    setProgress(null);

    try {
      const data = await programmer.readFlash(startAddr, readLen, setProgress);
      setReadData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Read failed at 0x${startAddr.toString(16)}`);
    } finally {
      setReading(false);
    }
  }, [programmer]);

  const handleReadAprom = useCallback(() => {
    const startAddr = parseInt(address, 16) || 0;
    const readLen = parseInt(effectiveLength, 10) || 0;
    if (readLen <= 0) return;
    handleRead(startAddr, readLen);
  }, [address, effectiveLength, handleRead]);

  const handleReadLdrom = useCallback(() => {
    if (!selectedChip?.ldromMaxSize) return;
    // LDROM sits at the end of flash space
    const ldromStart = selectedChip.flashSize;
    handleRead(ldromStart, selectedChip.ldromMaxSize);
  }, [selectedChip, handleRead]);

  const handleDownload = useCallback(() => {
    if (!readData || !selectedChip) return;
    const blob = new Blob([readData as BlobPart], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedChip.name}_readback_0x${address.toUpperCase().padStart(4, '0')}.bin`;
    a.click();
    URL.revokeObjectURL(url);
  }, [readData, selectedChip, address]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t('flash8051.readbackTitle')}
      </h3>

      <div className="space-y-4">
        {/* Address & length inputs */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t('readback.address')}
            </label>
            <div className="flex items-center">
              <span className="text-sm text-gray-400 mr-1">0x</span>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="0000"
                disabled={!isReady || reading}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm font-mono disabled:opacity-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t('readback.length')}
            </label>
            <input
              type="text"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              placeholder={selectedChip ? `${selectedChip.flashSize}` : '0'}
              disabled={!isReady || reading}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm font-mono disabled:opacity-50"
            />
          </div>
        </div>

        {/* Progress bar */}
        {progress && reading && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 dark:text-gray-400">
                {t('flash8051.readFlash')}
              </span>
              <span className="text-gray-400">{progress.percent.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-sm p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Read data info */}
        {readData && (
          <div className="text-sm p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
            Read {readData.length} bytes successfully
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleReadAprom}
            disabled={!isReady || reading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {reading ? t('common.loading') : t('flash8051.readAprom')}
          </button>
          {selectedChip?.ldromMaxSize && (
            <button
              onClick={handleReadLdrom}
              disabled={!isReady || reading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {t('flash8051.readLdrom')}
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={!readData || reading}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {t('readback.download')}
          </button>
        </div>

        {/* Size info */}
        {selectedChip && (
          <p className="text-xs text-gray-400">
            {t('flash8051.readRange', {
              start: `0x${address.toUpperCase().padStart(4, '0')}`,
              length: effectiveLength,
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReadbackPanel8051;
